import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import {
  find,
  flow,
  getOr,
  identity,
  keyBy,
  map,
  mapValues,
  merge,
  pickBy,
  sortBy,
  get,
  mapKeys,
} from 'lodash/fp';

import ItemAttribute from './ItemAttribute';
import itemAttributeShape from '../../../../../prop-types/itemAttributeShape';
import { prettyRenderFraction } from '../../../../../../../../../javascript/bcom/pages/pip/pipApp/dimensions/Fractions';

import { AttributesWrapper, MeasuredOn } from './styles';
import { useModalProviderContext } from '../../../../ModalProvider';
import { confirmDeleteAttributeModal } from '../../../../ModalProvider/modalNames';

const fractionConverter = value => `${prettyRenderFraction(value)}"`;

const mapKeyNames = keyMap => mapKeys(key => keyMap[key] || key);

const ItemAttributes = ({
  itemId,
  attributes,
  specMappings,
  windowCategoryMappings,
  mountTypeMappings,
  setItemAttribute,
  setLoading,
  globalError,
}) => {
  const { setIsOpen } = useModalProviderContext(confirmDeleteAttributeModal);

  const importantAttributes = useMemo(
    () => flow(
      keyBy('specName'),
      pickBy(({ displayPriority }) => displayPriority >= 100),
      mapValues(flow(
        mapKeyNames({ specName: 'name' }),
        spec => ({
          value: flow(
            find({ name: spec.name }),
            getOr('No', 'value'),
          )(attributes),
          ...spec,
        }),
      )),
    )(specMappings),
    [specMappings, attributes],
  );

  const valueMappers = useMemo(
    () => ({
      category: value => getOr(value, `${value}.categoryName`, windowCategoryMappings),
      mountType: value => getOr(value, value, mountTypeMappings),
      height: fractionConverter,
      width: fractionConverter,
      depth: fractionConverter,
    }),
    [windowCategoryMappings, mountTypeMappings],
  );

  const deleteAttribute = useCallback((attributeName) => {
    setIsOpen(true, {
      displayName: flow(
        find({ name: attributeName }),
        getOr(attributeName, 'displayName'),
      )(attributes),
      onSubmit: () => {
        setLoading(true);

        setItemAttribute(null, itemId, attributeName)
          .then(() => {
            setIsOpen(false);
            setLoading(false);
          })
          .catch(err => globalError(err));
      },
    });
  }, [attributes]);

  const shownAttributes = useMemo(
    () => flow(
      keyBy('name'),
      pickBy(({ displayPriority }) => displayPriority >= 50),
      pickBy('normalizedValue'),
      merge(importantAttributes),
      sortBy('sortIndex'),
      map(({
        name,
        displayName,
        value,
      }) => (
        <ItemAttribute
          key={name}
          name={name}
          label={displayName || name}
          value={(valueMappers[name] || identity)(value)}
          onDelete={deleteAttribute}
        />)),
    )(attributes),
    [attributes, importantAttributes, valueMappers, specMappings],
  );

  const measuredOn = useMemo(() => flow(
    find({ name: 'measureDate' }),
    get('value'),
  )(attributes));

  return (
    <AttributesWrapper>
      <div>
        {shownAttributes}
      </div>
      {measuredOn && <MeasuredOn>Measured on {format(measuredOn, 'MMMM D, YYYY')}</MeasuredOn>}
    </AttributesWrapper>
  );
};
ItemAttributes.propTypes = {
  itemId: PropTypes.string.isRequired,
  attributes: PropTypes.arrayOf(itemAttributeShape).isRequired,
  setItemAttribute: PropTypes.func.isRequired,
  specMappings: PropTypes.shape({}).isRequired,
  windowCategoryMappings: PropTypes.shape({}).isRequired,
  mountTypeMappings: PropTypes.shape({}).isRequired,
  setLoading: PropTypes.func.isRequired,
  globalError: PropTypes.func.isRequired,
};

export default ItemAttributes;
